import React, { useState, useEffect } from 'react';
import { useWallet } from '../hooks/useWallet';

const API = 'https://api.amitis.network';

export default function Faucet() {
  const { address, refresh, walletType } = useWallet();
  const [status, setStatus]   = useState(null);
  const [claiming, setClaiming] = useState(false);
  const [result, setResult]   = useState(null);
  const [inputAddr, setInputAddr] = useState('');

  useEffect(() => {
    const addr = address || inputAddr;
    if (addr) loadStatus(addr);
  }, [address, inputAddr]);

  async function loadStatus(addr) {
    try {
      const res = await fetch(`${API}/status/${addr}`);
      const data = await res.json();
      setStatus(data);
    } catch {}
  }

  async function claim() {
    const addr = address || inputAddr.trim();
    if (!addr) return;
    setClaiming(true);
    setResult(null);
    try {
      const res = await fetch(`${API}/claim`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ address: addr }),
      });
      const data = await res.json();
      if (data.txhash || data.success) {
        setResult({ ok: true, msg: `✓ ${status?.gift_amount_amts || 10} AMTS sent!`, hash: data.txhash });
        await refresh();
        await loadStatus(addr);
      } else {
        setResult({ ok: false, msg: data.error || data.message || 'Claim failed.' });
      }
    } catch (e) {
      setResult({ ok: false, msg: e.message || 'Network error.' });
    }
    setClaiming(false);
  }

  const giftAmt = status?.gift_amount_amts ?? 10;
  const canClaim = status?.can_claim !== false;

  return (
    <div className="page-content fade-up" style={{ maxWidth: 560 }}>
      <div className="faucet-hero">
        <div className="faucet-amount">{giftAmt}</div>
        <div className="faucet-denom">AMTS / CLAIM</div>
        <div className="faucet-desc">
          Free AMTS to get started on the Amitis Network.<br />
          80% of the network fee is rebated to you automatically.
        </div>
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-header">
          <div className="card-title">Claim AMTS</div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {address && (
              <span className="badge badge-green" style={{ fontSize: 10 }}>
                {walletType === 'native' ? '✦ Native' : '🔑 Keplr'}
              </span>
            )}
            {status && (
              <span className={`badge ${canClaim ? 'badge-green' : 'badge-muted'}`}>
                {canClaim ? '● Available' : '⏳ On Cooldown'}
              </span>
            )}
          </div>
        </div>

        {!address && (
          <div className="input-group">
            <label className="input-label">Your Amitis Address</label>
            <input
              className="input-field"
              placeholder="amitis1..."
              value={inputAddr}
              onChange={e => setInputAddr(e.target.value)}
              onBlur={() => inputAddr && loadStatus(inputAddr)}
            />
          </div>
        )}

        {status && (
          <div style={{ marginBottom: 16 }}>
            <div className="data-row">
              <span className="data-label">Address</span>
              <span className="data-value" style={{ fontSize: 11 }}>
                {(address || inputAddr).slice(0,14)}…
              </span>
            </div>
            {!canClaim && (
              <div className="data-row">
                <span className="data-label">Next claim</span>
                <span className="data-value">{status.next_claim_human || 'Check back later'}</span>
              </div>
            )}
            <div className="data-row">
              <span className="data-label">Total claimed</span>
              <span className="data-value green">{status.total_claimed_amts || 0} AMTS</span>
            </div>
          </div>
        )}

        <button
          className="btn btn-primary btn-full"
          onClick={claim}
          disabled={claiming || !canClaim}
          style={{ opacity: (!canClaim || claiming) ? 0.5 : 1 }}
        >
          {claiming ? <><div className="spinner"></div> Claiming…</> : `Claim ${giftAmt} AMTS`}
        </button>

        {result && (
          <div style={{
            marginTop: 14,
            padding: '12px 16px',
            borderRadius: 10,
            background: result.ok ? 'var(--green-dim)' : 'var(--red-dim)',
            border: `1px solid ${result.ok ? 'var(--green-border)' : 'rgba(248,113,113,0.25)'}`,
            color: result.ok ? 'var(--green)' : 'var(--red)',
            fontFamily: 'DM Mono, monospace',
            fontSize: 12,
          }}>
            {result.msg}
            {result.hash && (
              <div style={{ marginTop: 6, opacity: 0.7, fontSize: 10 }}>
                TX: {result.hash.slice(0, 20)}…
              </div>
            )}
          </div>
        )}
      </div>

      <div className="card">
        <div className="card-title" style={{ marginBottom: 14 }}>How it works</div>
        <div className="data-row">
          <span className="data-label">Amount per claim</span>
          <span className="data-value green">{giftAmt} AMTS</span>
        </div>
        <div className="data-row">
          <span className="data-label">Cooldown period</span>
          <span className="data-value">336 hours (14 days)</span>
        </div>
        <div className="data-row">
          <span className="data-label">Fee rebate</span>
          <span className="data-value green">80% auto-returned</span>
        </div>
        <div className="data-row">
          <span className="data-label">Geographic restriction</span>
          <span className="data-value">US only</span>
        </div>
      </div>
    </div>
  );
}
